import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {CircularProgress} from '@mui/material';
import {QueryClient, QueryClientProvider} from 'react-query';
import {
    MANAGEMENT_OVERVIEW_ERROR_MESSAGE,
    MANAGEMENT_OVERVIEW_NO_DATA_MESSAGE,
    ManagementOverviewRequestParams,
} from './types/ManagementOverviewReport.types';
import {
    useManagementOverviewReport,
} from './hooks/useManagementOverviewReport';
import {
    createInitialDailyWeekPages,
    moveLastDailyWeekToNextPage,
    pageHasVerticalOverflow,
    sortStationsByName,
} from './utils/managementOverviewPagination';
import {buildExpectedManagementOverviewChartIds} from './utils/managementOverviewChartIds';
import {markManagementOverviewChartReady} from './utils/managementOverviewChartReady';
import ManagementOverviewPageOne from './components/ManagementOverviewPageOne';
import ManagementOverviewPageTwo from './components/ManagementOverviewPageTwo';
import './managementOverviewReport.css';

interface ManagementOverviewReportPageProps {
    requestParams?: ManagementOverviewRequestParams;
    invalidParams?: boolean;
}

type DailyPartitionsByStation = Record<string, ReturnType<typeof createInitialDailyWeekPages>>;

interface ChartReadiness {
    reportKey: string;
    readyIds: Set<string>;
}

interface DailyPaginationState {
    reportKey: string;
    partitionsByStation: DailyPartitionsByStation;
}

function ManagementOverviewReportContent({
    requestParams,
    invalidParams = false,
}: ManagementOverviewReportPageProps) {
    const hookResult = useManagementOverviewReport(
        invalidParams ? {} : (requestParams ?? {})
    );

    const sortedStations = useMemo(
        () => sortStationsByName(hookResult?.data?.stations ?? []),
        [hookResult?.data?.stations]
    );

    const stationIdsKey = useMemo(
        () => sortedStations.map((station) => station.stationId).join(','),
        [sortedStations]
    );
    const reportKey = useMemo(() => [
        stationIdsKey,
        hookResult?.data?.referencePeriod.endDateTime ?? '',
        hookResult?.data?.generatedOn ?? '',
    ].join('|'), [
        hookResult?.data?.generatedOn,
        hookResult?.data?.referencePeriod.endDateTime,
        stationIdsKey,
    ]);

    const expectedChartIds = useMemo(
        () => buildExpectedManagementOverviewChartIds(sortedStations.map((s) => s.stationId)),
        [stationIdsKey]
    );

    const [chartReadiness, setChartReadiness] = useState<ChartReadiness>(() => ({
        reportKey: '',
        readyIds: new Set(),
    }));
    const [dailyPagination, setDailyPagination] = useState<DailyPaginationState>(() => ({
        reportKey: '',
        partitionsByStation: {},
    }));
    const [validatedLayoutKey, setValidatedLayoutKey] = useState('');
    const [measurementRevision, setMeasurementRevision] = useState(0);
    const reportRootRef = useRef<HTMLDivElement>(null);

    const handleChartReady = useCallback((chartId: string) => {
        setChartReadiness((current) => {
            const readyIds = current.reportKey === reportKey
                ? current.readyIds
                : new Set<string>();
            return {
                reportKey,
                readyIds: markManagementOverviewChartReady(readyIds, chartId),
            };
        });
    }, [reportKey]);

    const chartsReady =
        hookResult.pageState === 'sucesso' &&
        chartReadiness.reportKey === reportKey &&
        expectedChartIds.length > 0 &&
        expectedChartIds.every((id) => chartReadiness.readyIds.has(id));
    const reportReady = chartsReady && validatedLayoutKey === reportKey;
    const activePartitionsByStation = dailyPagination.reportKey === reportKey
        ? dailyPagination.partitionsByStation
        : {};

    useEffect(() => {
        const handleResize = () => {
            setValidatedLayoutKey('');
            setMeasurementRevision((current) => current + 1);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!chartsReady || !reportRootRef.current) {
            setValidatedLayoutKey('');
            return;
        }

        let cancelled = false;
        let firstFrame = 0;
        let secondFrame = 0;

        const validatePages = () => {
            firstFrame = window.requestAnimationFrame(() => {
                secondFrame = window.requestAnimationFrame(() => {
                    if (cancelled || !reportRootRef.current) {
                        return;
                    }

                    const pages = Array.from(
                        reportRootRef.current.querySelectorAll<HTMLElement>('.report-page')
                    );
                    const overflowingPage = pages.find((page) => pageHasVerticalOverflow(page));
                    if (!overflowingPage) {
                        setValidatedLayoutKey(reportKey);
                        return;
                    }

                    setValidatedLayoutKey('');
                    if (overflowingPage.dataset.pageKind !== 'daily') {
                        return;
                    }

                    const stationId = overflowingPage.dataset.stationId;
                    const pageIndex = Number(overflowingPage.dataset.dailyPageIndex);
                    const station = sortedStations.find(
                        (candidate) => String(candidate.stationId) === stationId
                    );
                    if (!stationId || !station || !Number.isInteger(pageIndex)) {
                        return;
                    }

                    setDailyPagination((current) => {
                        const currentPartitions = current.reportKey === reportKey
                            ? current.partitionsByStation
                            : {};
                        const currentPages = currentPartitions[stationId]
                            ?? createInitialDailyWeekPages(station.dailyWeeks);
                        const nextPages = moveLastDailyWeekToNextPage(currentPages, pageIndex);
                        if (nextPages === currentPages) {
                            return current;
                        }
                        return {
                            reportKey,
                            partitionsByStation: {
                                ...currentPartitions,
                                [stationId]: nextPages,
                            },
                        };
                    });
                });
            });
        };

        const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
        if (fonts?.ready) {
            void fonts.ready.then(() => {
                if (!cancelled) {
                    validatePages();
                }
            });
        } else {
            validatePages();
        }

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(firstFrame);
            window.cancelAnimationFrame(secondFrame);
        };
    }, [
        chartsReady,
        dailyPagination,
        measurementRevision,
        reportKey,
        sortedStations,
    ]);

    const handlePrint = () => {
        window.print();
    };

    if (invalidParams || hookResult.pageState === 'erro') {
        return (
            <div className="management-overview-status" role="alert">
                {MANAGEMENT_OVERVIEW_ERROR_MESSAGE}
            </div>
        );
    }

    if (hookResult.pageState === 'carregando') {
        return (
            <div className="management-overview-status" role="status" aria-live="polite">
                <CircularProgress size={32} aria-label="Carregando relatório" />
                <span>Carregando relatório...</span>
            </div>
        );
    }

    if (hookResult.pageState === 'sem-dados') {
        return (
            <div className="management-overview-status" role="status">
                {MANAGEMENT_OVERVIEW_NO_DATA_MESSAGE}
            </div>
        );
    }

    const report = hookResult.data;
    if (!report) {
        return null;
    }

    return (
        <div
            key={reportKey}
            ref={reportRootRef}
            className="management-overview-root"
            data-report-ready={reportReady ? 'true' : 'false'}
        >
            <div className="management-overview-print-actions no-print">
                <button
                    type="button"
                    className="management-overview-print-button"
                    onClick={handlePrint}
                    disabled={!reportReady}
                >
                    Imprimir / Salvar PDF
                </button>
            </div>

            {sortedStations.map((station, stationIndex) => {
                const dailyPartitions = activePartitionsByStation[String(station.stationId)]
                    ?? createInitialDailyWeekPages(station.dailyWeeks);
                const firstDailyPartition = dailyPartitions[0] ?? [];
                const continuationPartitions = dailyPartitions.slice(1);
                const showFullTitle = stationIndex === 0;

                const pageOne = (
                    <ManagementOverviewPageOne
                        key={`${station.stationId}-page-one`}
                        report={report}
                        station={station}
                        showFullTitle={showFullTitle}
                        onChartReady={handleChartReady}
                    />
                );

                const pageTwo = (
                    <ManagementOverviewPageTwo
                        key={`${station.stationId}-page-two`}
                        report={report}
                        station={station}
                        dailyPageIndex={0}
                        dailyWeeks={firstDailyPartition.map((block) => ({
                            weekName: block.weekName,
                            days: block.days,
                            rows: block.rows,
                        }))}
                        onChartReady={handleChartReady}
                    />
                );

                const continuationPages = continuationPartitions.map((partition, partitionIndex) => (
                    <ManagementOverviewPageTwo
                        key={`${station.stationId}-continuation-${partitionIndex}`}
                        report={report}
                        station={station}
                        includeCharts={false}
                        dailyPageIndex={partitionIndex + 1}
                        dailyWeeks={partition.map((block) => ({
                            weekName: block.weekName,
                            days: block.days,
                            rows: block.rows,
                        }))}
                    />
                ));

                return (
                    <React.Fragment key={station.stationId}>
                        {pageOne}
                        {pageTwo}
                        {continuationPages}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default function ManagementOverviewReportPage(props: ManagementOverviewReportPageProps) {
    const queryClient = useMemo(() => new QueryClient(), []);

    return (
        <QueryClientProvider client={queryClient}>
            <ManagementOverviewReportContent {...props} />
        </QueryClientProvider>
    );
}
